import { useAuth0 } from "@auth0/auth0-react";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { createContext, useEffect, useMemo, useRef, useState } from "react";
import {
  msspDashboardKeys,
  platformUsersKeys,
  userAccessControlKeys,
} from "../api/queryKeys";
import {
  MSSP_MULTI_TENANT_PAGES,
  MSSP_PLATFORM_ACCESS_ROLES,
  MSSP_TOKEN_DELIMITER,
} from "../utils/constants";
import { getEnvStorageKey } from "../utils/environment";

import { useLocation } from "react-router-dom";
const CURRENT_TENANT_STORAGE_KEY = getEnvStorageKey("current_tenant_id");
const SELECTED_TENANTS_STORAGE_KEY = getEnvStorageKey("selected_tenant_ids");

export const AxiosContext = createContext({
  instance: null,
  isMSSP: false,
  msspId: null,
  currentTenantId: null,
  rerenderKey: 0,
  switchTenant: (tenantId, withoutRerender = false) => {},
  isAllTenants: false,
  setIsAllTenants: (isAllTenants) => {},
  selectedTenantIds: [],
  setSelectedTenantIds: (tenantIds) => {},
  hasMultipleSelectedTenants: false,
  setPlatformUserFromBootstrap: (user) => {},
});

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_ENDPOINT,
  headers: {
    "Content-Type": "application/json",
  },
});

export const AxiosProvider = ({ children }) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [isMSSP, setIsMSSP] = useState(false);
  const [msspId, setMsspId] = useState(null);
  const [tokenTenant, setTokenTenant] = useState(null);
  const [rerenderKey, setRerenderKey] = useState(0);
  const [isAllTenants, setIsAllTenants] = useState(true);
  const [cancelRerender, setCancelRerender] = useState(false);
  const [platformUserSet, setPlatformUserSet] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState(() => {
    return localStorage.getItem(CURRENT_TENANT_STORAGE_KEY);
  });
  const [selectedTenantIds, setSelectedTenantIds] = useState(() => {
    const stored = localStorage.getItem(SELECTED_TENANTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const selectedTenantIdsRef = useRef(selectedTenantIds);
  const location = useLocation();
  const isMsspMultiTenantPage = MSSP_MULTI_TENANT_PAGES.some((page) =>
    location.pathname.includes(page)
  );

  const hasMultipleSelectedTenants = useMemo(
    () =>
      selectedTenantIds &&
      selectedTenantIds.length > 1 &&
      isMsspMultiTenantPage,
    [selectedTenantIds, isMsspMultiTenantPage]
  );
  useEffect(() => {
    selectedTenantIdsRef.current = selectedTenantIds;
    localStorage.setItem(
      SELECTED_TENANTS_STORAGE_KEY,
      JSON.stringify(selectedTenantIds)
    );
  }, [selectedTenantIds]);

  const tokenTenantRef = useRef(
    tokenTenant || localStorage.getItem(CURRENT_TENANT_STORAGE_KEY)
  );

  useEffect(() => {
    tokenTenantRef.current =
      tokenTenant || localStorage.getItem(CURRENT_TENANT_STORAGE_KEY);
  }, [tokenTenant]);

  useEffect(() => {
    const interceptorId = instance.interceptors.request.use(async (config) => {
      try {
        const token = await getAccessTokenSilently();
        const currentTokenTenant = tokenTenantRef.current;

        if (!token) {
          throw new Error(
            "Failed to retrieve authentication token. User may not be authenticated."
          );
        }

        config.headers["Authorization"] = currentTokenTenant
          ? `Bearer ${token}${MSSP_TOKEN_DELIMITER}${currentTokenTenant}`
          : `Bearer ${token}`;

        if (selectedTenantIdsRef.current.length > 0) {
          config.headers["X-Selected-Tenants"] =
            selectedTenantIdsRef.current.join(",");
        }
      } catch (error) {
        console.error("Failed to get access token:", error);
        return Promise.reject(error);
      }
      return config;
    });

    return () => {
      instance.interceptors.request.eject(interceptorId);
    };
  }, [getAccessTokenSilently]);

  const setPlatformUserFromBootstrap = (platformUser) => {
    if (platformUserSet || !platformUser) return;

    if (MSSP_PLATFORM_ACCESS_ROLES.includes(platformUser.access_role)) {
      setIsMSSP(true);
      setMsspId(platformUser.mssp_id);
      const storedTenantId = localStorage.getItem(CURRENT_TENANT_STORAGE_KEY);
      if (!storedTenantId) {
        setCurrentTenantId(platformUser.customer_id);
      }
    }
    setPlatformUserSet(true);
  };

  // Set token tenant if changed
  useEffect(() => {
    if (isMSSP && currentTenantId && currentTenantId !== tokenTenant) {
      setTokenTenant(currentTenantId);
      localStorage.setItem(CURRENT_TENANT_STORAGE_KEY, currentTenantId);

      // Clear only tenant-specific queries except msspDashboardKey
      queryClient.removeQueries({
        predicate: (query) => {
          // Guard against undefined queryKey
          if (!query.queryKey || !Array.isArray(query.queryKey)) {
            return true;
          }

          const msspDashboardKey = Object.values(msspDashboardKeys).map(
            (query) => {
              if (typeof query === "function") {
                return query().join(",");
              }
              return query.join(",");
            }
          );
          if (msspDashboardKey.includes(query.queryKey.join(","))) return false;
          if (
            [...platformUsersKeys.all, ...userAccessControlKeys.all].some(
              (queryKey) => query.queryKey.join(",").includes(queryKey)
            )
          )
            return false;
          return true;
        },
      });

      // Refetch all queries except msspDashboardKey
      queryClient.refetchQueries({
        predicate: (query) => {
          // Guard against undefined queryKey
          if (!query.queryKey || !Array.isArray(query.queryKey)) {
            return false;
          }

          const msspDashboardKey = Object.values(msspDashboardKeys).map(
            (query) => {
              if (typeof query === "function") {
                return query().join(",");
              }
              return query.join(",");
            }
          );
          if (msspDashboardKey.includes(query.queryKey.join(","))) return false;
          return true;
        },
      });

      if (!cancelRerender) {
        // Trigger a full app rerender (this ensures data fetched without useQuery is re-fetched)
        setRerenderKey((prev) => prev + 1);
      }
    }
  }, [currentTenantId, isMSSP]);

  // Update the local storage
  const switchTenant = (tenantId, withoutRerender = false) => {
    setCurrentTenantId(tenantId);
    setCancelRerender(withoutRerender);
  };

  return (
    <AxiosContext.Provider
      value={{
        instance,
        isMSSP,
        msspId,
        currentTenantId,
        switchTenant,
        rerenderKey,
        isAllTenants,
        setIsAllTenants,
        selectedTenantIds,
        setSelectedTenantIds,
        hasMultipleSelectedTenants,
        setPlatformUserFromBootstrap,
      }}
    >
      {children}
    </AxiosContext.Provider>
  );
};
